/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { complianceApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import HelpButton from "@/components/HelpButton";
import {
    ShieldCheck, Scales, Newspaper, WarningOctagon, ArrowSquareOut, ArrowsClockwise,
} from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Compliance() {
    return (
        <div className="p-4 md:p-8 max-w-[1300px] mx-auto" data-testid="compliance-page">
            <div className="rule-line pt-3 mb-6">
                <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-1">Compliance</div>
                <div className="flex items-center gap-2">
                    <h1 className="font-serif text-4xl tracking-tight">BCI Compliance & Regulatory Feed</h1>
                    <HelpButton topic="compliance" testid="help-compliance" />
                </div>
                <p className="text-sm text-muted-foreground mt-1">Self-audit against Bar Council of India rules + stay current with Supreme Court, BCI and government notifications.</p>
            </div>
            <Tabs defaultValue="rules">
                <TabsList>
                    <TabsTrigger value="rules" data-testid="comp-tab-rules"><Scales size={13} className="mr-1" /> BCI rules</TabsTrigger>
                    <TabsTrigger value="audit" data-testid="comp-tab-audit"><ShieldCheck size={13} className="mr-1" /> Self-audit</TabsTrigger>
                    <TabsTrigger value="conflict" data-testid="comp-tab-conflict"><WarningOctagon size={13} className="mr-1" /> Conflict scan</TabsTrigger>
                    <TabsTrigger value="feed" data-testid="comp-tab-feed"><Newspaper size={13} className="mr-1" /> Regulatory feed</TabsTrigger>
                </TabsList>
                <TabsContent value="rules" className="pt-4"><RulesTab /></TabsContent>
                <TabsContent value="audit" className="pt-4"><AuditTab /></TabsContent>
                <TabsContent value="conflict" className="pt-4"><ConflictTab /></TabsContent>
                <TabsContent value="feed" className="pt-4"><FeedTab /></TabsContent>
            </Tabs>
        </div>
    );
}

function RulesTab() {
    const [rules, setRules] = useState([]);
    useEffect(() => { complianceApi.bciRules().then(setRules).catch(() => {}); }, []);
    return (
        <div className="space-y-2" data-testid="bci-rules-list">
            {rules.map((r) => (
                <div key={r.rule_no} className="border border-border bg-white rounded-md p-3">
                    <div className="text-xs font-mono text-muted-foreground">{r.rule_no}</div>
                    <div className="font-medium">{r.topic}</div>
                    <div className="text-sm text-muted-foreground mt-1">{r.summary}</div>
                </div>
            ))}
        </div>
    );
}

function AuditTab() {
    const [v, setV] = useState({ website_text: "", bio: "" });
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);

    const run = async () => {
        setBusy(true);
        try {
            const r = await complianceApi.selfAudit(v);
            setResult(r);
            toast.success(`${r.findings?.length || 0} finding(s)`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Audit failed");
        }
        setBusy(false);
    };

    const sevColor = {
        critical: "border-destructive/50 bg-destructive/10",
        high: "border-destructive/40 bg-destructive/5",
        medium: "border-amber-300 bg-amber-50/40",
        low: "border-emerald-300 bg-emerald-50/40",
    };

    return (
        <div className="space-y-4" data-testid="audit-tab">
            <div className="border border-border bg-white rounded-md p-3">
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Paste your public-facing marketing copy</div>
                <Textarea rows={5} placeholder="Website 'About' text + advertising copy" value={v.website_text} onChange={(e) => setV({ ...v, website_text: e.target.value })} data-testid="audit-website" />
                <div className="text-xs text-muted-foreground mt-2 mb-1">Bio / chamber profile</div>
                <Textarea rows={3} placeholder="LinkedIn bio, chamber profile, social media headline" value={v.bio} onChange={(e) => setV({ ...v, bio: e.target.value })} data-testid="audit-bio" />
                <div className="flex justify-end pt-2">
                    <Button onClick={run} disabled={busy} data-testid="audit-run-btn"><ShieldCheck size={14} /> {busy ? "Scanning…" : "Run self-audit"}</Button>
                </div>
            </div>
            {result && (
                <div className="space-y-2" data-testid="audit-result">
                    {!result.findings.length ? (
                        <div className="border border-emerald-300 bg-emerald-50 rounded-md p-3 text-sm text-emerald-800">
                            <ShieldCheck size={14} weight="fill" className="inline mr-1" /> No BCI compliance issues detected in the supplied text.
                        </div>
                    ) : (
                        result.findings.map((f, i) => (
                            <div key={i} className={`border rounded-md p-3 ${sevColor[f.severity] || "border-border"}`} data-testid={`audit-finding-${i}`}>
                                <div className="flex items-center gap-2 text-xs">
                                    <span className="chip chip-doc">{f.severity}</span>
                                    <span className="font-mono uppercase tracking-[0.15em] text-muted-foreground">{f.rule_no}</span>
                                </div>
                                <div className="font-medium mt-1">{f.topic}</div>
                                <div className="text-sm mt-1">{f.issue}</div>
                                {f.suggested_fix && <div className="text-xs mt-1"><b>Fix:</b> {f.suggested_fix}</div>}
                            </div>
                        ))
                    )}
                    <div className="text-xs text-muted-foreground">Checked rules: {result.checked_rules?.length || 0} · {result.audited_at?.slice(0, 19)}</div>
                </div>
            )}
        </div>
    );
}

function ConflictTab() {
    const [v, setV] = useState({ proposed_client_name: "", proposed_opposing_parties: "" });
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);
    const run = async () => {
        if (!v.proposed_client_name) { toast.error("Client name required"); return; }
        setBusy(true);
        try {
            const r = await complianceApi.conflictScan({
                proposed_client_name: v.proposed_client_name,
                proposed_opposing_parties: v.proposed_opposing_parties.split(/\n+/).map((x) => x.trim()).filter(Boolean),
            });
            setResult(r);
            if (!r.findings.length) toast.success("No conflicts found");
            else toast.message(`${r.findings.length} potential conflict(s)`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
        setBusy(false);
    };
    const sev = {
        critical: "border-destructive/50 bg-destructive/10",
        high: "border-destructive/40 bg-destructive/5",
        medium: "border-amber-300 bg-amber-50/40",
        low: "border-emerald-300 bg-emerald-50/40",
    };
    return (
        <div className="space-y-4" data-testid="conflict-tab">
            <div className="border border-border bg-white rounded-md p-3">
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Proposed engagement</div>
                <Input placeholder="Proposed client name" value={v.proposed_client_name} onChange={(e) => setV({ ...v, proposed_client_name: e.target.value })} data-testid="conflict-client" />
                <div className="text-xs text-muted-foreground mt-3 mb-1">Opposing parties — one per line</div>
                <Textarea rows={3} placeholder="Acme Pvt Ltd&#10;State Bank of India" value={v.proposed_opposing_parties} onChange={(e) => setV({ ...v, proposed_opposing_parties: e.target.value })} data-testid="conflict-opposing" />
                <div className="flex justify-end pt-2"><Button onClick={run} disabled={busy} data-testid="conflict-run-btn">{busy ? "Scanning…" : "Run conflict scan"}</Button></div>
            </div>
            {result && (
                <div className="space-y-2" data-testid="conflict-result">
                    {!result.findings.length ? (
                        <div className="border border-emerald-300 bg-emerald-50 rounded-md p-3 text-sm text-emerald-800">No conflicts found in your roster.</div>
                    ) : (
                        result.findings.map((f, i) => (
                            <div key={i} className={`border rounded-md p-3 ${sev[f.severity]}`} data-testid={`conflict-finding-${i}`}>
                                <div className="flex items-center gap-2 text-xs">
                                    <span className="chip chip-doc">{f.severity}</span>
                                    <span className="font-mono uppercase tracking-[0.15em] text-muted-foreground">{f.type}</span>
                                </div>
                                <div className="text-sm mt-1">{f.message}</div>
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}

function FeedTab() {
    const [sources, setSources] = useState({ defaults: [], custom: [] });
    const [source, setSource] = useState("sci_judgments");
    const [feed, setFeed] = useState(null);
    const [busy, setBusy] = useState(false);

    const loadSources = async () => {
        try { setSources(await complianceApi.feedSources()); }
        catch { /* swallow */ }
    };
    useEffect(() => { loadSources(); }, []);

    const fetchFeed = async () => {
        setBusy(true);
        try {
            const r = await complianceApi.feed(source);
            setFeed(r);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Fetch failed");
        }
        setBusy(false);
    };

    useEffect(() => { fetchFeed(); }, [source]);

    const allSources = [...sources.defaults, ...sources.custom];

    return (
        <div className="space-y-4" data-testid="feed-tab">
            <div className="flex items-center gap-2 flex-wrap">
                <Select value={source} onValueChange={setSource}>
                    <SelectTrigger className="w-[320px]" data-testid="feed-source"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        {allSources.map((s) => <SelectItem key={s.key || s.id} value={s.key || s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={fetchFeed} disabled={busy} data-testid="feed-refresh"><ArrowsClockwise size={13} /> Refresh</Button>
                {feed?.cached && <span className="text-xs text-muted-foreground">cached @ {feed.fetched_at?.slice(0, 16).replace("T", " ")}</span>}
                {feed?.fetch_error && <span className="text-xs text-destructive">stale (couldn't reach: {feed.fetch_error.slice(0, 60)})</span>}
            </div>
            {busy && <div className="text-sm text-muted-foreground">Fetching feed…</div>}
            {!busy && (!feed || !feed.items?.length) ? (
                <div className="border border-dashed border-border rounded-md p-6 text-sm text-muted-foreground text-center">No items returned for this source.</div>
            ) : feed && (
                <div className="space-y-2" data-testid="feed-items">
                    {feed.items.map((it, i) => (
                        <a key={i} href={it.link} target="_blank" rel="noreferrer" className="border border-border bg-white rounded-md p-3 block hover:bg-muted/30" data-testid={`feed-item-${i}`}>
                            <div className="flex items-start gap-2">
                                <div className="flex-1 min-w-0">
                                    <div className="font-medium">{it.title}</div>
                                    {it.snippet && <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{it.snippet}</div>}
                                    {it.published && <div className="text-xs text-muted-foreground mt-1">{it.published}</div>}
                                </div>
                                <ArrowSquareOut size={14} className="text-muted-foreground shrink-0 mt-1" />
                            </div>
                        </a>
                    ))}
                </div>
            )}
        </div>
    );
}
